import { useEffect, useRef, useState } from 'react';
import styles from './Toast.module.css';

interface Props {
  visible: boolean;
  onDismiss: () => void;
  sessionId?: string | null;
  sessionTitle?: string | null;
  onNavigate?: (sessionId: string) => void;
}

export default function Toast({ visible, onDismiss, sessionId, sessionTitle, onNavigate }: Props) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [inDom, setInDom] = useState(false);
  // Snapshot props when toast becomes visible so content doesn't change during exit animation.
  const displaySessionIdRef = useRef<string | null>(null);
  const displaySessionTitleRef = useRef<string | null>(null);

  useEffect(() => {
    if (visible && sessionId) {
      displaySessionIdRef.current = sessionId;
      displaySessionTitleRef.current = sessionTitle ?? null;
      setInDom(true);
    }
  }, [visible, sessionId, sessionTitle]);

  useEffect(() => {
    if (!visible) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(onDismiss, 8000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [visible, onDismiss]);

  if (!inDom) return null;

  const displaySessionId = displaySessionIdRef.current;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: toast auto-dismisses after 8s; the inner Go-to-conversation button is the keyboard-accessible action
    <div
      className={`${styles.toast} ${!visible ? styles.toastExit : ''}`}
      onClick={onDismiss}
      role="status"
      onAnimationEnd={(e) => {
        // Only act on the toast div's own animation, not bubbled child events.
        if (e.target === e.currentTarget && !visible) setInDom(false);
      }}
    >
      <svg
        aria-hidden="true"
        className={styles.icon}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="8" cy="8" r="6.5" />
        <polyline points="5 8 7 10 11 6" />
      </svg>
      <span>
        Response ready
        {displaySessionTitleRef.current ? ` in "${displaySessionTitleRef.current}"` : ''}
      </span>
      {displaySessionId && onNavigate && (
        <button
          type="button"
          className={styles.link}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(displaySessionId);
          }}
        >
          Go to conversation →
        </button>
      )}
    </div>
  );
}
